import React, { useState, useRef, useMemo, useCallback } from 'react';
import Image from 'next/image';
import {
  X,
  Search,
  Users,
  Palette,
  Package,
  MoreHorizontal,
} from 'lucide-react';
import { useProducts } from '@/data/product';
import { Input, Button, Tabs, Tab } from '@nextui-org/react';
import { useDebounce } from '@/lib/hooks/use-debounce';
import { SortOrder } from '@/types';
import {
  characterJson,
  stylesJson,
  productJson,
  otherJson,
} from '@/components/home/featured-generators';
import { useNSFWBlur } from '@/lib/hooks/use-nsfw-blur';
import NSFWToggle from '../nsfw/nsfw-toggle';
import { cn } from '@/lib/utils';
import { useNSFWSettings } from '@/lib/hooks/use-nsfw-settings';
import { NSFW_TAGS, getNSFWCategory } from '@/lib/constants/nsfw-tags';

interface LoraModel {
  id: string;
  name: string;
  image: string;
  trigger_word: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (model: LoraModel | null) => void;
}

const ModelCard = ({
  model,
  onSelect,
  onClose,
}: {
  model: any;
  onSelect: (model: LoraModel) => void;
  onClose: () => void;
}) => {
  const { shouldBlur } = useNSFWBlur(model);
  return (
    <div
      onClick={() => {
        onSelect({
          id: model.id || model.slug,
          name: model.name,
          image:
            model.image?.thumbnail ||
            model.image?.original ||
            '/placeholder.svg',
          trigger_word: model.trigger_word || '',
        });
        onClose();
      }}
      className="group relative cursor-pointer overflow-hidden rounded-xl bg-light-100 transition-all hover:shadow-lg dark:bg-dark-300"
    >
      <div className="aspect-[4/3] w-full overflow-hidden">
        <Image
          src={
            model.image?.thumbnail ||
            model.image?.original ||
            '/placeholder.svg'
          }
          alt={model.name}
          width={400}
          height={300}
          className={cn(
            'h-full w-full object-cover duration-300 transition-transform group-hover:scale-110',
            shouldBlur && 'blur-md'
          )}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 duration-300 transition-opacity group-hover:opacity-100" />
      </div>

      <div className="absolute bottom-0 left-0 right-0 p-4 text-white opacity-0 duration-300 transition-opacity group-hover:opacity-100">
        <h3 className="text-sm font-medium line-clamp-1">{model.name}</h3>
        <p className="mt-1 text-xs text-gray-200 line-clamp-1">
          {model.trigger_word || 'Click to select'}
        </p>
      </div>

      <div className="p-4">
        <h3 className="font-medium line-clamp-1 group-hover:text-brand dark:text-light-400">
          {model.name}
        </h3>
        <p className="mt-1 text-xs text-gray-500 line-clamp-1 dark:text-dark-700">
          {model.trigger_word || 'Click to select'}
        </p>
      </div>

      {shouldBlur && (
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 rounded-lg bg-gradient-to-br from-violet-500/20 to-fuchsia-500/20 backdrop-blur-md">
          <div className="bg-dot-pattern absolute inset-0 opacity-5" />
          <div className="relative flex flex-col items-center gap-2">
            <div className="mb-1 h-1.5 w-12 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500" />
            <p className="text-sm font-medium text-foreground/90">
              Adult Content
            </p>
            <div className="rounded-lg border border-violet-500/20 bg-background/90 px-4 py-2 shadow-lg backdrop-blur-sm">
              <NSFWToggle size="lg" className="min-w-[120px]" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const LoraModelSelector = ({ isOpen, onClose, onSelect }: Props) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('featured');
  const debouncedSearch = useDebounce(searchTerm, 500);
  const searchRef = useRef<HTMLInputElement>(null);
  const { settings } = useNSFWSettings();
  const isSafeVersion = process.env.NEXT_PUBLIC_IS_SAFE === 'true';

  // Filter function to handle NSFW content based on user settings
  const filterNsfw = useCallback(
    (models: any[]) => {
      return models.filter((model) => {
        if (!model.tag_slugs) return true;

        // In safe version, always exclude all NSFW content
        if (isSafeVersion) {
          return !model.tag_slugs.some(
            (tag: string) => getNSFWCategory(tag) !== null
          );
        }

        // Filter based on user settings
        const hasExplicit = model.tag_slugs.some(
          (tag: string) => getNSFWCategory(tag) === 'explicit'
        );
        const hasMature = model.tag_slugs.some(
          (tag: string) => getNSFWCategory(tag) === 'mature'
        );

        if (hasExplicit && !settings.explicit) return false;
        if (hasMature && !settings.mature) return false;

        return true;
      });
    },
    [settings, isSafeVersion]
  );

  // Featured models from the featured-generators.ts file
  const featuredModelsData = useMemo(() => {
    return {
      character: filterNsfw(characterJson),
      styles: filterNsfw(stylesJson),
      product: filterNsfw(productJson || []),
      more: filterNsfw(otherJson),
    };
  }, [filterNsfw]);

  // API models query
  const { products: searchedProducts, isLoading } = useProducts(
    {
      is_custom_ai_model: true,
      limit: 12,
      name: debouncedSearch,
    },
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled: Boolean(debouncedSearch),
    }
  );

  // Determine which models to display
  const displayedModels = useMemo(() => {
    if (debouncedSearch) {
      return filterNsfw(searchedProducts || []);
    }

    if (activeTab === 'featured') {
      // Combine a few from each category for the featured tab
      return filterNsfw([
        ...characterJson.slice(0, 3),
        ...stylesJson.slice(0, 3),
        ...(productJson || []).slice(0, 2),
        ...otherJson.slice(0, 2),
      ]);
    }

    return featuredModelsData[activeTab] || [];
  }, [
    activeTab,
    debouncedSearch,
    searchedProducts,
    featuredModelsData,
    filterNsfw,
  ]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50"
      onClick={handleBackdropClick}
    >
      <div className="relative h-[80vh] w-full max-w-3xl rounded-xl bg-white p-6 shadow-xl dark:bg-dark-200">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <X className="h-6 w-6" />
        </button>

        <h2 className="mb-4 text-xl font-semibold dark:text-light-400">
          Select LoRA Model
        </h2>

        <div className="relative mb-6">
          <Input
            ref={searchRef}
            type="text"
            placeholder="Search models..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            startContent={<Search className="h-4 w-4 text-gray-400" />}
            classNames={{
              input: 'pl-3 dark:text-light-400',
              inputWrapper:
                'bg-default-400/20 dark:bg-default-400/20 hover:bg-default-200 dark:hover:bg-default-400/30',
            }}
          />
        </div>

        {!debouncedSearch && (
          <div className="mb-4">
            <Tabs
              selectedKey={activeTab}
              onSelectionChange={(key) => setActiveTab(key as string)}
              color="primary"
              variant="light"
              classNames={{
                base: 'w-full',
                tabList: 'bg-gray-100/80 p-1 dark:bg-dark-400/30 rounded-full',
                tab: 'data-[selected=true]:bg-brand data-[selected=true]:text-white rounded-full dark:text-light-400 dark:data-[selected=false]:hover:text-white',
                tabContent: 'group-data-[selected=true]:text-white',
                cursor: 'bg-brand',
              }}
            >
              <Tab key="featured" title="Featured" />
              <Tab
                key="character"
                title={
                  <div className="flex items-center gap-1">
                    <Users size={16} /> Characters
                  </div>
                }
              />
              <Tab
                key="styles"
                title={
                  <div className="flex items-center gap-1">
                    <Palette size={16} /> Styles
                  </div>
                }
              />
              <Tab
                key="product"
                title={
                  <div className="flex items-center gap-1">
                    <Package size={16} /> Products
                  </div>
                }
              />
              <Tab
                key="more"
                title={
                  <div className="flex items-center gap-1">
                    <MoreHorizontal size={16} /> More
                  </div>
                }
              />
            </Tabs>
          </div>
        )}

        <div className="h-[calc(80vh-240px)] max-h-[600px] overflow-y-auto px-1">
          {isLoading ? (
            <div className="flex h-40 items-center justify-center">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-brand"></div>
            </div>
          ) : displayedModels.length === 0 ? (
            <div className="flex h-40 flex-col items-center justify-center text-center">
              <p className="text-gray-500 dark:text-gray-400">
                No models found
              </p>
              <Button
                className="mt-4 bg-brand text-white"
                onClick={() => {
                  setSearchTerm('');
                  setActiveTab('featured');
                }}
              >
                View featured models
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
              {displayedModels.map((model, index) => (
                <ModelCard
                  key={model.id || model.slug || index}
                  model={model}
                  onSelect={onSelect}
                  onClose={onClose}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoraModelSelector;
